import { useCallback, useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, withoutSyncBookkeeping } from '@/src/renderer/lib/db/indexeddb'
import { notifySyncQueued } from '@/src/renderer/lib/sync/sync-notify'
import { isCompletedTask, isDormantTask, isRecurringTask } from '@/src/renderer/lib/db/taskFilters'
import { isFavoriteTask } from '@/src/renderer/lib/favorites'
import { useActivePausedLiveQuery } from '@/src/renderer/lib/db/useActivePausedLiveQuery'
import { useStableEntityArray, baseEntityIdentity } from '@/src/renderer/lib/db/useStableEntityArray'
import { useStableLiveQuery } from '@/src/renderer/lib/db/useStableLiveQuery'
import { useSharedTags } from '@/src/renderer/lib/db/sharedTags'
import type { HabitLog, Setting, SyncQueueItem, Task } from '@/src/renderer/lib/db/models'
import { useUserDisplayProfile } from '@/src/renderer/hooks/useUserDisplayProfile'
import { usePreference } from '@/src/renderer/hooks/usePreference'
import { useProject, useSectionsByProject } from '@/src/renderer/lib/db/hooks'
import { saveProfileAvatar, saveProfileName } from '@/src/renderer/lib/profile'
import { getAvatarImageUrl, loadAvatarCategories, loadAvatars, searchAvatars } from '@/src/renderer/lib/supabase/avatars'
import type { DataSource } from './dataSource'
import { useStableSidebarCounts } from './sidebarCounts'
import { useMidnightRefresh } from '@/src/renderer/lib/date/useMidnightRefresh'
import { nextTagColor } from './tagColors'
import { isIntroContent } from '@/src/renderer/lib/import/importedFrom'
import { gainsStartTime } from '@/src/renderer/lib/reminders/reminderFromDraft'
import { isOpenTask, latestCanvasNoteList, latestFocusedTaskList, latestTaskList, searchCanvasNoteList, searchTaskList } from '@/src/renderer/lib/search/matchSearch'

/** Nur neu abonnieren, wenn sich die MENGE der Kennungen ändert, nicht bei jedem frischen Array. */
const idSetKey = (ids: readonly string[]) => [...ids].sort().join(',')
const splitIdSetKey = (key: string) => (key ? key.split(',') : [])

const settingType = (value: unknown): Setting['type'] =>
  Array.isArray(value) ? 'array' : value !== null && typeof value === 'object' ? 'object'
    : typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string'

async function setStoredSetting(key: string, value: unknown): Promise<void> {
  await db.setSetting(key, value, settingType(value))
}

/** The app's data source: Dexie, with live queries. Behaves as the components did before phase 7. */
export const dexieDataSource: DataSource = {
  useTags() {
    return useSharedTags()
  },

  async createTag(name, options) {
    const count = await db.tags.count()
    return db.createTag({ name: name.trim(), color: options?.color ?? nextTagColor(count), description: null, importedFrom: options?.importedFrom ?? null })
  },

  async getTags() {
    return (await db.getAllTags()).filter(tag => !tag.deletedAt)
  },

  async deleteUnusedTags(ids) {
    const used = new Set<string>()
    const collect = (row: { tags?: string[]; deletedAt: Date | null }) => {
      if (!row.deletedAt) for (const id of row.tags ?? []) used.add(id)
    }
    // Einmalig und selten — alle Zeilen lesen statt eines Index auf `tags`.
    await db.tasks.each(collect)
    await db.subtasks.each(collect)
    const unused = ids.filter(id => !used.has(id))
    for (const id of unused) await db.deleteTag(id)
    return unused
  },

  async findImported(source) {
    const prefix = `${source}:`
    const collect = async (table: 'projects' | 'sections' | 'tasks' | 'subtasks' | 'tags' | 'goals' | 'habits', keep: (row: { parentTaskId?: string | null }) => boolean = () => true) => {
      // Über den Index (Dexie v39, Ziele/Gewohnheiten v49): ein Import schlägt Hunderte Zeilen auf einmal nach.
      const rows = (await db.table(table).where('importedFrom').startsWith(prefix).toArray()).filter(keep)
      // `lifeSpace` nur bei Projekten: Neue Aufgaben eines schon übernommenen Projekts gehören in dessen Bereich.
      // `estimatedDuration` und die Zähler nur bei Aufgaben: Der Import trägt dort Tages-Dauern (T32 #197)
      // und Todoists Zähler (#198) nach; die alte `subtasks`-Tabelle hat diese Felder nicht und bleibt ohne.
      return new Map(rows.map(r => [r.importedFrom as string, {
        id: r.id as string,
        deleted: r.deletedAt != null,
        ...(r.lifeSpace && table === 'projects' ? { lifeSpace: r.lifeSpace } : {}),
        ...(table === 'tasks'
          ? { estimatedDuration: r.estimatedDuration ?? null, completedCount: r.completedCount ?? null, postponedCount: r.postponedCount ?? null }
          : {}),
      }]))
    }
    const tasks = await collect('tasks', row => row.parentTaskId == null)
    // Unteraufgaben sind Aufgaben mit Elternteil (T14 #179). Aus der alten Tabelle kommt
    // dazu, was noch nicht umgewandelt ist.
    const subtasks = new Map([...await collect('subtasks'), ...await collect('tasks', row => row.parentTaskId != null)])
    return {
      projects: await collect('projects'),
      sections: await collect('sections'),
      tasks,
      subtasks,
      tags: await collect('tags'),
      goals: await collect('goals'),
      habits: await collect('habits'),
    }
  },

  useSubtasks(taskId) {
    return useLiveQuery(() => (taskId ? db.getSubtasks(taskId) : undefined), [taskId])
  },

  useSubtaskTree(taskId) {
    return useLiveQuery(() => (taskId ? db.getSubtaskTree(taskId) : undefined), [taskId])
  },

  useSubtaskTrees(taskIds) {
    const key = idSetKey(taskIds)
    return useLiveQuery(() => db.getSubtaskTrees(splitIdSetKey(key)), [key])
  },

  useCommentCounts(taskIds) {
    const key = idSetKey(taskIds)
    return useLiveQuery(() => db.countTaskCommentsByTask(splitIdSetKey(key)), [key])
  },

  useRemindersByTask(taskIds) {
    const key = idSetKey(taskIds)
    return useLiveQuery(() => db.getRemindersByTask(splitIdSetKey(key)), [key])
  },

  getSubtask(id) {
    return db.getSubtask(id)
  },

  createSubtask(taskId, title) {
    return db.createSubtask(taskId, title)
  },

  insertSubtaskAfter(taskId, afterId, title) {
    return db.insertSubtaskAfter(taskId, afterId, title)
  },

  async moveSubtask(id, afterId) {
    await db.moveSubtask(id, afterId)
  },

  async restoreSubtask(id, cascade) {
    await db.restoreSubtask(id, cascade)
  },

  async updateSubtask(id, changes) {
    await db.updateSubtask(id, changes)
  },

  deleteSubtask(id) {
    return db.deleteSubtask(id)
  },

  async updateTask(id, changes) {
    await db.updateTask(id, changes)
  },

  // Eine Transaktion samt Sync-Einträgen: bricht eine Zeile, bleibt keine halb geändert.
  async updateTasks(changes) {
    const now = new Date()
    await db.transaction('rw', db.tasks, db.syncQueue, db.taskReminders, async () => {
      for (const { id, changes: fields } of changes) {
        const before = fields.recurrence != null || fields.startTime !== undefined || fields.status !== undefined || fields.startDate !== undefined
          ? await db.tasks.get(id)
          : undefined
        const counters = before ? db.taskCounter.count(before, fields, now) : {}
        await db.tasks.update(id, { ...fields, ...counters, updatedAt: now, lastModified: now, synced: false })
        await db.queueSyncChange('update', 'tasks', { localId: id })
        await db.moveChildrenWithParent(id, fields)
        if (fields.recurrence != null) await db.dropChildRecurrence(id, before?.recurrence ?? null)
        // Wie `db.updateTask`: Wer jetzt eine Uhrzeit am Wann hat, bekommt die Standard-Erinnerung.
        if (before && gainsStartTime(before, { startTime: fields.startTime })) await db.addStandardReminderIfMissing(id)
      }
    })
  },

  getTask(id) {
    return db.getTask(id)
  },

  getTaskAncestors(id) {
    return db.getAncestorTasks(id)
  },

  useTaskAncestors(id) {
    return useLiveQuery(() => (id ? db.getAncestorTasks(id) : []), [id])
  },

  useTask(id) {
    return useLiveQuery(() => (id ? db.getTask(id) : null), [id])
  },

  useTasksByIds(ids) {
    const key = ids.join(',')
    return useLiveQuery(async () => {
      const rows = await db.tasks.bulkGet([...ids])
      return new Map(rows.filter((t): t is Task => t != null).map(t => [t.id, t]))
    }, [key])
  },

  useTaskChains(ids) {
    const key = ids.join(',')
    return useLiveQuery(() => db.getTaskChains(ids), [key])
  },

  createTask(task) {
    return db.createTask(task)
  },

  async copySubtasks(fromTaskId, toTaskId, options) {
    const copied = new Map<string, string>()
    await db.copySubtasks(fromTaskId, toTaskId, options, undefined, copied)
    return copied
  },

  nestTasks(ids, parentId, afterId) {
    return db.nestTasks(ids, parentId, afterId)
  },

  unnestTask(id, at) {
    return db.unnestTask(id, at)
  },

  indentSubtasks(ids) {
    return db.indentSubtasks(ids)
  },

  outdentSubtasks(ids) {
    return db.outdentSubtasks(ids)
  },

  restorePlacements(placements) {
    return db.restorePlacements(placements)
  },

  reorderTasks(updates) {
    return db.reorderTasks(updates)
  },

  async restoreTask(snapshot, cascade) {
    await db.tasks.put({ ...snapshot, deletedAt: null, synced: false })
    // Eine Zeile, die die Cloud nie gesehen hat, braucht ein `create`.
    await db.queueSyncChange(
      snapshot.cloudId ? 'update' : 'create',
      'tasks',
      { localId: snapshot.id, cloudId: snapshot.cloudId },
    )
    if (!cascade) return
    await db.restoreTaskSubtasks(cascade.subtaskIds)
    await db.relinkCanvasNotes(cascade.unlinkedNoteIds, snapshot.id)
    await db.restoreTaskTimeEntries(cascade.timeEntryIds ?? [])
    await db.restoreComments(cascade.commentIds)
    await db.restoreReminders(cascade.reminderIds ?? [])
  },

  useRunningFocus() {
    return useLiveQuery(() => db.getRunningFocus(), [])
  },

  startFocus(taskId) {
    return db.startFocus(taskId)
  },

  async endFocus(at) {
    await db.endFocus(at)
  },

  addTimeEntry(taskId, startTime, endTime) {
    return db.addTimeEntry(taskId, startTime, endTime)
  },

  useFocusSeconds(taskIds) {
    // Nur neu abonnieren, wenn sich die MENGE der Kennungen ändert, nicht bei jedem
    // frischen Array — wie bei `useHabitLogs`.
    const key = [...taskIds].sort().join(',')
    return useStableLiveQuery(() => db.getFocusSecondsByTask(key ? key.split(',') : []), [key])
  },

  useTaskComments(taskId) {
    return useLiveQuery(() => (taskId ? db.getTaskComments(taskId) : undefined), [taskId])
  },

  useCommentCount(taskId) {
    return useLiveQuery(() => (taskId ? db.countTaskComments(taskId) : 0), [taskId], 0) ?? 0
  },

  createComment(taskId, draft) {
    return db.createComment(taskId, draft)
  },

  updateComment(id, content) {
    return db.updateComment(id, content)
  },

  deleteComment(id) {
    return db.deleteComment(id)
  },

  restoreComment(id) {
    return db.restoreComments([id])
  },

  useProjectComments(projectId) {
    return useLiveQuery(() => (projectId ? db.getProjectComments(projectId) : undefined), [projectId])
  },

  useProjectCommentCount(projectId) {
    return useLiveQuery(() => (projectId ? db.countProjectComments(projectId) : 0), [projectId], 0) ?? 0
  },

  createProjectComment(projectId, draft) {
    return db.createProjectComment(projectId, draft)
  },

  updateProjectComment(id, content) {
    return db.updateProjectComment(id, content)
  },

  deleteProjectComment(id) {
    return db.deleteProjectComment(id)
  },

  restoreProjectComment(id) {
    return db.restoreProjectComments([id])
  },

  moveComments(moves) {
    return db.moveComments(moves)
  },

  useTaskReminders(taskId) {
    return useLiveQuery(() => (taskId ? db.getTaskReminders(taskId) : undefined), [taskId])
  },

  useLiveReminders() {
    return useLiveQuery(() => db.getLiveReminders(), [])
  },

  createReminder(taskId, draft) {
    return db.createReminder(taskId, draft)
  },

  updateReminder(id, changes) {
    return db.updateReminder(id, changes)
  },

  deleteReminder(id) {
    return db.deleteReminder(id)
  },

  restoreReminder(id) {
    return db.restoreReminders([id])
  },

  moveReminders(moves) {
    return db.moveReminders(moves)
  },

  copyReminders(copies) {
    return db.copyReminders(copies)
  },

  async recordRewardUsage(rewardId) {
    await db.recordRewardUsage(rewardId)
  },

  getRecentRewardUsages(limit) {
    return db.getRecentRewardUsages(limit)
  },

  useProjects() {
    return useLiveQuery(() => db.getAllProjects(), [])
  },

  async getProjects() {
    return (await db.getAllProjects()).filter(project => !project.deletedAt)
  },

  // Ein Durchlauf über die Tabelle: Titel und Beschreibung haben keinen Index, und Akzente zählen nicht.
  async searchTasks(query, options) {
    return searchTaskList(await db.tasks.filter(t => !t.deletedAt).toArray(), query, options)
  },

  async searchCanvasNotes(query, options) {
    const [notes, projects] = await Promise.all([
      db.canvasNotes.filter(n => !n.deletedAt).toArray(),
      db.projects.toArray(),
    ])
    return searchCanvasNoteList(notes, new Map(projects.map(p => [p.id, p])), query, options)
  },

  async latestTasks(options) {
    return latestTaskList(await db.tasks.filter(t => !t.deletedAt).toArray(), options)
  },

  async latestCanvasNotes(options) {
    const [notes, projects] = await Promise.all([
      db.canvasNotes.filter(n => !n.deletedAt).toArray(),
      db.projects.toArray(),
    ])
    return latestCanvasNoteList(notes, new Map(projects.map(p => [p.id, p])), options)
  },

  async latestFocusedTasks(options) {
    const entries = await db.timeEntries.filter(e => !e.deletedAt).toArray()
    const tasks = await db.tasks.bulkGet([...new Set(entries.map(e => e.taskId))])
    return latestFocusedTaskList(entries, new Map(tasks.filter((t): t is Task => !!t).map(t => [t.id, t])), options)
  },

  // Über den Hook aus `lib/db/hooks` — die Tests der Projektseite setzen ihr Projekt dort ein.
  useProject(id) {
    const { data, loading } = useProject(id ?? null)
    if (!id) return null
    return loading ? undefined : data
  },

  async updateProject(id, changes) {
    await db.updateProject(id, changes)
  },

  useProjectsByLifeSpace(lifeSpace) {
    return useLiveQuery(() => db.getProjectsByLifeSpace(lifeSpace), [lifeSpace])
  },

  useCanvasNotes(projectId) {
    return useLiveQuery(() => (projectId ? db.getCanvasNotes(projectId) : undefined), [projectId])
  },

  createCanvasNote(note) {
    return db.createCanvasNote(note)
  },

  updateCanvasNote(id, changes) {
    return db.updateCanvasNote(id, changes)
  },

  setCanvasNoteMeasuredHeight(id, height) {
    return db.setCanvasNoteMeasuredHeight(id, height)
  },

  setCanvasNoteMeasuredSize(id, size) {
    return db.setCanvasNoteMeasuredSize(id, size)
  },

  deleteCanvasNote(id) {
    return db.deleteCanvasNote(id)
  },

  getCanvasNotesByIds(ids) {
    return db.canvasNotes.bulkGet(ids)
  },

  deleteTask(id) {
    return db.deleteTask(id)
  },

  restoreTasks(ids) {
    return db.restoreTasks(ids)
  },

  // Supabase online, Dexie offline — the hook has always done both.
  useUserDisplayProfile(displaySize, userId) {
    return useUserDisplayProfile(userId, displaySize)
  },

  saveProfileName(fullName) {
    return saveProfileName(fullName)
  },

  saveProfileAvatar(avatarPresetId) {
    return saveProfileAvatar(avatarPresetId)
  },

  // Dexie-Cache, im Hintergrund mit Supabase abgeglichen; Bilder vom CDN.
  async loadAvatarCatalog() {
    const [categories, avatars] = await Promise.all([loadAvatarCategories(), loadAvatars()])
    return { categories, avatars }
  },

  searchAvatars(query, language) {
    return searchAvatars(query, language)
  },

  avatarImageUrl(avatar, displaySize) {
    return getAvatarImageUrl(avatar, displaySize)
  },

  useSidebarCounts() {
    // Die Woche liest der Index `startDate`; nach Mitternacht beobachtet Dexie sonst die von gestern (#309).
    const day = useMidnightRefresh()
    const counts = useLiveQuery(() => db.getSidebarCounts(), [day])
    return useStableSidebarCounts(counts)
  },

  useUpcomingTaskCount(lifeSpace) {
    const day = useMidnightRefresh()
    return useLiveQuery(() => db.getUpcomingTaskCountByLifeSpace(lifeSpace), [lifeSpace, day], 0) ?? 0
  },

  useTasksByProject(projectId) {
    const tasks = useStableLiveQuery(() => (projectId ? db.getTasksByProject(projectId) : undefined), [projectId])
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  useAllSectionsByProject(projectId) {
    const sections = useStableLiveQuery(() => (projectId ? db.getSectionsByProject(projectId) : undefined), [projectId])
    return useStableEntityArray(sections, baseEntityIdentity)
  },

  getSection(id) {
    return db.getSection(id)
  },

  getTasksByProject(projectId) {
    return db.getTasksByProject(projectId)
  },

  getQuickNotes(lifeSpace) {
    return db.getQuickNotesByLifeSpace(lifeSpace)
  },

  getSectionsByProject(projectId) {
    return db.getSectionsByProject(projectId)
  },

  createSection(section) {
    return db.createSection(section)
  },

  // Die Aufgaben VOR dem Löschen merken: `deleteSection` nimmt ihnen den Abschnitt.
  async deleteSection(id) {
    const tasks = await db.tasks.where('sectionId').equals(id).filter(t => !t.deletedAt).toArray()
    await db.deleteSection(id)
    return { taskIds: tasks.map(t => t.id) }
  },

  async restoreSection(snapshot, taskIds) {
    // Offene 'delete'-Einträge entfernen, sonst löschte der Sync den Abschnitt in der Cloud trotzdem.
    const pendingDeletes = await db.syncQueue
      .filter(q => q.table === 'sections' && q.action === 'delete' && q.data?.localId === snapshot.id)
      .toArray()
    for (const entry of pendingDeletes) {
      if (entry.id != null) await db.syncQueue.delete(entry.id)
    }
    await db.sections.put({ ...snapshot, deletedAt: null, synced: false })
    await db.queueSyncChange(
      snapshot.cloudId ? 'update' : 'create',
      'sections',
      { localId: snapshot.id, cloudId: snapshot.cloudId },
    )
    for (const taskId of taskIds) {
      await db.tasks.update(taskId, { sectionId: snapshot.id, synced: false })
      await db.queueSyncChange('update', 'tasks', { localId: taskId })
    }
  },

  archiveSection(id) {
    return db.archiveSection(id)
  },

  unarchiveSection(id, reopen) {
    return db.unarchiveSection(id, reopen)
  },

  useSectionsByProject(projectId) {
    return useSectionsByProject(projectId)
  },

  async updateSection(id, changes) {
    await db.updateSection(id, changes)
  },

  createProject(project) {
    return db.createProject(project)
  },

  deleteProject(id) {
    return db.deleteProject(id)
  },

  getProjectIsEmpty(id) {
    return db.isProjectEmpty(id)
  },

  moveProjectInTree(projectId, target) {
    return db.moveProjectInTree(projectId, target)
  },

  moveProjectToLifeSpace(projectId, lifeSpace) {
    return db.moveProjectToLifeSpace(projectId, lifeSpace)
  },

  restoreProjectLifeSpace(move) {
    return db.restoreProjectLifeSpace(move)
  },

  restoreProject(id, cascade) {
    return db.restoreProject(id, cascade)
  },

  useLifeSpaceProjects(lifeSpace) {
    return useStableLiveQuery(
      () => db.projects.where('lifeSpace').equals(lifeSpace).filter(p => !p.deletedAt).toArray(),
      [lifeSpace],
    )
  },

  useQuickNotes(lifeSpace) {
    const tasks = useStableLiveQuery(() => db.getQuickNotesByLifeSpace(lifeSpace), [lifeSpace])
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  // Ruhend und Wiederkehrend: Das Feld ist nicht indiziert, also ein voller Scan — der pausiert,
  // solange der Tab verborgen ist.
  usePausedTasks(live) {
    const tasks = useActivePausedLiveQuery(() => db.tasks.filter(isDormantTask).toArray(), [], live)
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  // Über den Index auf `favoriteOrder`: Aufgaben ohne Stern (`null`) liegen gar nicht darin.
  useFavoriteTasks(live) {
    const tasks = useActivePausedLiveQuery(
      () => db.tasks.where('favoriteOrder').aboveOrEqual(-Infinity).filter(isFavoriteTask).toArray(),
      [],
      live,
    )
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  useRecurringTasks(live) {
    const tasks = useActivePausedLiveQuery(() => db.tasks.filter(isRecurringTask).toArray(), [], live)
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  // Über den Index auf `completedAt`; offene Aufgaben (`null`) liegen gar nicht darin (= `isCompletedTask`).
  useCompletedTasks(live) {
    const tasks = useActivePausedLiveQuery(
      () => db.tasks.where('completedAt').above(new Date(0)).filter(isCompletedTask).toArray(),
      [],
      live,
    )
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  useOpenTasks(live) {
    const tasks = useActivePausedLiveQuery(() => db.tasks.filter(task => !task.deletedAt && isOpenTask(task)).toArray(), [], live)
    return useStableEntityArray(tasks, baseEntityIdentity)
  },

  // Zwei Bereichsabfragen über die Indizes auf `startDate` und `dueDate` (= `isInDateRange`):
  // Aufgaben außerhalb des Zeitraums kommen nie in den Speicher.
  useTasksInDateRange(from, to, live) {
    const fromMs = from.getTime()
    const toMs = to.getTime()
    const byStart = useStableEntityArray(useActivePausedLiveQuery(
      () => db.tasks
        .where('startDate').between(new Date(fromMs), new Date(toMs), true, false)
        .filter(t => !t.deletedAt)
        .toArray(),
      [fromMs, toMs],
      live,
    ), baseEntityIdentity)
    // Nur ohne Beginn — sonst käme eine Aufgabe aus der ersten Abfrage doppelt.
    const byDueOnly = useStableEntityArray(useActivePausedLiveQuery(
      () => db.tasks
        .where('dueDate').between(new Date(fromMs), new Date(toMs), true, false)
        .filter(t => !t.deletedAt && t.startDate == null)
        .toArray(),
      [fromMs, toMs],
      live,
    ), baseEntityIdentity)
    return useMemo(
      () => (byStart === undefined || byDueOnly === undefined ? undefined : [...byStart, ...byDueOnly]),
      [byStart, byDueOnly],
    )
  },

  useGoals(lifeSpace) {
    return useStableLiveQuery(() => db.getGoalsByLifeSpace(lifeSpace), [lifeSpace])
  },

  createGoal(goal) {
    return db.createGoal(goal)
  },

  async updateGoal(id, changes) {
    await db.updateGoal(id, changes)
  },

  deleteGoal(id) {
    return db.deleteGoal(id)
  },

  async restoreGoal(id) {
    await db.updateGoal(id, { deletedAt: null })
  },

  useHabits(lifeSpace) {
    return useStableLiveQuery(() => db.getHabitsByLifeSpace(lifeSpace, true), [lifeSpace])
  },

  useHabitLogs(habitIds) {
    // Nur neu abonnieren, wenn sich die MENGE der Kennungen ändert, nicht bei jedem frischen Array.
    const key = [...habitIds].sort().join(',')
    return useStableLiveQuery(async () => {
      const map = new Map<string, HabitLog[]>()
      for (const id of habitIds) map.set(id, [])
      if (habitIds.length === 0) return map
      const logs = await db.habitLogs.where('habitId').anyOf(habitIds).filter(l => !l.deletedAt).toArray()
      for (const log of logs) map.get(log.habitId)?.push(log)
      return map
    }, [key])
  },

  createHabit(habit) {
    return db.createHabit(habit)
  },

  async updateHabit(id, changes) {
    await db.updateHabit(id, changes)
  },

  deleteHabit(id) {
    return db.deleteHabit(id)
  },

  async setHabitLog(habitId, date, completed) {
    await db.setHabitLog(habitId, date, completed)
  },

  // Über den Hook — seine Tests und die der Belohnungs-Auswahl setzen dort an.
  usePreference<T>(key: string, defaultValue: T) {
    return usePreference<T>(key, defaultValue)
  },

  useSetting<T>(key: string, defaultValue: T) {
    // Live statt einmal gelesen: Seitenleiste, Seiten und das Fenster der Schnelleingabe lesen dieselben
    // Merker der Einführung (`lib/intro`) und müssen eine Änderung sofort sehen. `null` = nicht gespeichert.
    const stored = useLiveQuery(async () => (await db.getSetting(key)) ?? null, [key])
    const setValue = useCallback((next: T) => setStoredSetting(key, next), [key])
    return { value: stored ? (stored.value as T) : defaultValue, setValue, loaded: stored !== undefined }
  },

  async getSetting<T>(key: string, defaultValue: T) {
    return db.getSettingValue<T>(key, defaultValue)
  },

  setSetting<T>(key: string, value: T) {
    return setStoredSetting(key, value)
  },

  useAppIsEmpty() {
    // `first()` statt `count()`: Ein lebender Eintrag reicht, und meist steht er vorn.
    return useLiveQuery(async () => {
      const live = <T extends { deletedAt?: Date | null }>(row: T) => !row.deletedAt
      const found = await Promise.all([
        db.tasks.filter(task => live(task) && !isIntroContent(task)).first(),
        db.projects.filter(project => live(project) && !project.isSystem && !isIntroContent(project)).first(),
        db.goals.filter(live).first(),
        db.habits.filter(live).first(),
      ])
      return found.every(row => row === undefined)
    }, [])
  },

  async readBackupRows(options) {
    const keep = <T extends { deletedAt?: Date | null }>(rows: T[]) => (options?.includeDeleted ? rows : rows.filter(row => !row.deletedAt))
    const [
      tags, projects, sections, tasks, canvasNotes, taskComments, projectComments, taskReminders, timeEntries,
      goals, habits, habitLogs, settings, userPreferences, rewardUsages, profiles,
    ] = await Promise.all([
      db.tags.toArray(), db.projects.toArray(), db.sections.toArray(), db.tasks.toArray(), db.canvasNotes.toArray(),
      db.taskComments.toArray(), db.projectComments.toArray(), db.taskReminders.toArray(), db.timeEntries.toArray(),
      db.goals.toArray(), db.habits.toArray(), db.habitLogs.toArray(), db.settings.toArray(), db.userPreferences.toArray(),
      db.rewardUsages.toArray(), db.userProfiles.toArray(),
    ])
    const profile = profiles.find(p => p.id === options?.accountId) ?? profiles.find(p => p.id === 'offline-user') ?? profiles[0]
    return {
      tables: {
        tags: keep(tags),
        projects: keep(projects.filter(p => !p.isSystem)),
        sections: keep(sections),
        tasks: keep(tasks),
        canvasNotes: keep(canvasNotes),
        taskComments: keep(taskComments),
        projectComments: keep(projectComments),
        taskReminders: keep(taskReminders),
        // Ein laufender Abschnitt bleibt auf dem Gerät (ADR 0025) — er käme sonst als laufend zurück.
        timeEntries: keep(options?.includeDeleted ? timeEntries : timeEntries.filter(e => e.endTime != null)),
        goals: keep(goals),
        habits: keep(habits),
        habitLogs: keep(habitLogs),
        settings,
        userPreferences: keep(userPreferences),
        rewardUsages: keep(rewardUsages),
      },
      profile: profile
        ? { fullName: profile.fullName, displayName: profile.displayName, email: profile.email, avatarPresetId: profile.avatar_preset_id ?? null }
        : null,
    }
  },

  /*
    Eine Transaktion unter stillgelegter Buchführung: `createdAt` bleibt, die Zeitstempel setzen wir
    selbst. Offene Löschungen derselben Zeilen fallen weg — sonst löschte der Push sie gleich wieder.
    Die Warteschlange meldet sich erst am Ende, einmal.
  */
  async writeBackupRows(rows) {
    const now = new Date()
    const entries = Object.entries(rows).filter(([, list]) => list && list.length > 0) as Array<[keyof typeof rows, Array<{ id: string; cloudId?: string | null }>]>
    if (entries.length === 0) return
    const tables = entries.map(([name]) => db.table(name))
    await withoutSyncBookkeeping(() => db.transaction('rw', [...tables, db.syncQueue], async () => {
      const written = new Map<string, Set<string>>()
      for (const [name, list] of entries) {
        await db.table(name).bulkPut(list.map(row => ({ ...row, updatedAt: now, lastModified: now, synced: false, syncedAt: null })))
        written.set(name, new Set(list.map(row => row.id)))
      }
      const pendingDeletes = await db.syncQueue
        .filter(q => q.action === 'delete' && !q.synced && !!written.get(q.table)?.has(q.data?.localId))
        .toArray()
      await db.syncQueue.bulkDelete(pendingDeletes.flatMap(q => (q.id != null ? [q.id] : [])))
      for (const [name, list] of entries) {
        // `settings` ist rein lokal; alles andere hat einen Weg in die Cloud.
        if (name === 'settings') continue
        for (const row of list) {
          await db.queueSyncChange(row.cloudId ? 'update' : 'create', name as SyncQueueItem['table'], { localId: row.id }, { silent: true })
        }
      }
    }))
    notifySyncQueued()
  },
}
